import React, { useState, useEffect, useContext } from "react";
import { Container, Spinner } from "react-bootstrap";
import ActionComp from "../layouts/ActionComp";
import BreadcrumbComp from "../layouts/BreadcrumbComp";
import GridMaster from "./GridMaster";
import StatusRenderer from "./StatusRenderer.jsx";
import ActionRenderer from "./ActionRenderer";
import { exportToExcel } from "../utils/ExcelCreation";
import { getCurrentDateTime, formatDateInDDMMYYYY } from "../../functions/common-functions";
import AddEditContractMaster from "./AddEditContractMaster";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import successImage from "../../icons/success-tick-logo.svg";
import errorImage from "../../icons/error-tick-logo.svg";
import { AuthContext } from "../../contexts/AuthContext";
import { menuActions } from "../../store/menu-slice";
import { useDispatch } from "react-redux";
import { sortByPropertyInAscending } from "../../utils/Utility";
import { searchActions } from "../../store/grid-slice";
import { ALERT_PARAMS } from "../../utils/constant.js";
import { deleteContract, getAllContracts, updateContract } from "../../services/contract-master-service.js";
import { addLogDetails } from "../../services/logger-entry-service";

const ContractMaster = () => {
  const MySwal = withReactContent(Swal);
  const context = useContext(AuthContext);
  const userDetails = { userName: context.userName, role: context.role, userId: context.userId };
  const dispatch = useDispatch();

  const [rowData, setRowData] = useState({});
  const [filteredResult, setFilteredResult] = useState([]);
  const [filteredResulttemp, setFilteredResulttemp] = useState([]);
  const [filterColLength, setFilterColLength] = useState(0);
  const [action, setAction] = useState("");
  const [reload, setReload] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [resetFilter, setResetFilter] = useState(true);
  const [loading, setLoading] = useState(true);

  const columnDefs = [
    {
      headerName: "Status",
      field: "DeleteIndication",
      flex: false,
      maxWidth: 120,
      resizable: true,
      tooltipField: "DeleteIndication",
      cellRenderer: StatusRenderer,
      sort: 'asc'
    },
    {
      headerName: "Item Code",
      field: "ItemCode",
      minWidth: 150,
      tooltipField: "ItemCode",
      sort: 'asc'
    },
    {
      headerName: "Item Description",
      field: "ItemDescription",
      minWidth: 150,
      tooltipField: "ItemDescription",
    },
      {
      headerName: "Item Details",
      field: "ItemDetail",
      minWidth: 150,
      tooltipField: "ItemDetail",
    },
    {
      headerName: "Location",
      field: "Location",
      minWidth: 150,
      tooltipField: "Location",
    },
    {
      headerName: "Contract Period (months)",
      field: "ContractPeriod",
      minWidth: 150,
      tooltipField: "ContractPeriod",
    },
    {
      headerName: "GST Rate",
      field: "GSTRate",
      minWidth: 150,
      tooltipField: "GSTRate",
      valueFormatter: (params) => {
        return params.value ? `${params.value}%` : "";
      }
    },
    {
      headerName: "Contract Start Date",
      field: "ContractStartDate",
      minWidth: 150,
      tooltipField: "ContractStartDate",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      }
    },
    {
      headerName: "Expiry Date",
      field: "ExpiryDate",
      minWidth: 150,
      tooltipField: "ExpiryDate",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      }
    },
    {
      headerName: "Inserted By",
      field: "CreatedByName",
      minWidth: 150,
      tooltipField: "CreatedByName",
    },
    {
      headerName: "Inserted On",
      field: "CreatedOn",
      minWidth: 150,
      tooltipField: "CreatedOn",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      }
    },
    {
      headerName: "Modified By",
      field: "ModifiedByName",
      minWidth: 150,
      tooltipField: "ModifiedByName",
    },
    {
      headerName: "Modified On",
      field: "ModifiedOn",
      minWidth: 150,
      tooltipField: "ModifiedOn",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      }
    },
    {
      field: "",
      maxWidth: 120,
      cellRenderer: function (params) {
        return <ActionRenderer
          selectedData={params.data}
          handleEditEvent={handleEdit}
          handleCopyEvent={handleCopy} />
      },
      sortable: false,
      filter: false,
      pinned: 'right',
      resizable: false
    },
  ];

  const headerName = (field) => {
    const mapping = {
      "DeleteIndication": "Status",
      "ItemCode": "Item Code",
      "ItemDescription": "Item Description",
      "Location": "Location",
      "ContractPeriod": "Contract Period (months)",
      "GSTRate": "GST Rate",
      "ContractStartDate": "Contract Start Date",
      "ExpiryDate": "Expiry Date",
      "CreatedByName": "Inserted By",
      "CreatedOn": "Inserted On",
      "ModifiedByName": "Modified By",
      "ModifiedOn": "Modified On"
    };
    return mapping[field] || "No match found";
  };

  const fieldName = (header) => {
    const mapping = {
      "Status": "DeleteIndication",
      "Item Code": "ItemCode",
      "Item Description": "ItemDescription",
      "Location": "Location",
      "Contract Period (months)": "ContractPeriod",
      "GST Rate": "GSTRate",
      "Contract Start Date": "ContractStartDate",
      "Expiry Date": "ExpiryDate",
      "Inserted By": "CreatedByName",
      "Inserted On": "CreatedOn",
      "Modified By": "ModifiedByName",
      "Modified On": "ModifiedOn"
    };
    return mapping[header] || "No match found";
  };

  const fetchContractMaster = async () => {
    setLoading(true);
    const result = await getAllContracts();
    const data = Array.isArray(result) ? result : [];
    data.sort(sortByPropertyInAscending("DeleteIndication"));
    data.sort((a, b) => {
      if (a.DeleteIndication === b.DeleteIndication) {
        return (a.ItemCode || "").localeCompare(b.ItemCode || "");
      }
      return 0;
    });
    setRowData(data)
    setFilteredResult(data);
    setExcelData(data);
    setResetFilter(!resetFilter);
    setLoading(false);
  };

  useEffect(() => {
    dispatch(menuActions.setMenu("Contract Master"));
    dispatch(searchActions.setSearch(""));
    fetchContractMaster();
  }, [reload]);

  const handleEdit = (row) => {
    setAction("Edit");
    setRowData(row);
    setShowForm(true);
  }

  const handleCopy = (row) => {
    setAction("Copy");
    setRowData(row);
    setShowForm(true);
  }


  const handleGlobalExport = async () => {
    let filename = "ContractMaster";
    let excelDownoadData = [];

    excelData.forEach(element => {
      let dataEntry = {
        DeleteIndication: element.DeleteIndication,
        ItemCode: element.ItemCode,
        ItemDescription: element.ItemDescription,
        ItemDetail: element.ItemDetail,
        Location: Array.isArray(element.Location) ? element.Location.join(", ") : element.Location,
        ContractPeriod: element.ContractPeriod,
        GSTRate: element.GSTRate,
        ContractStartDate: element.ContractStartDate ? formatDateInDDMMYYYY(element.ContractStartDate.toString()) : "",
        ExpiryDate: element.ExpiryDate ? formatDateInDDMMYYYY(element.ExpiryDate.toString()) : "",
        CreatedByName: element.CreatedByName,
        CreatedOn: element.CreatedOn ? formatDateInDDMMYYYY(element.CreatedOn.toString()) : "",
        ModifiedByName: element.ModifiedByName,
        ModifiedOn: element.ModifiedOn ? formatDateInDDMMYYYY(element.ModifiedOn.toString()) : ""
      };
      excelDownoadData.push(dataEntry);
    });

    let colName = ["Status", "Item Code", "Item Description", "Item Details", "Location", "Contract Period (months)", "GST Rate", "Contract Start Date", "Expiry Date", "Inserted By", "Inserted On", "Modified By", "Modified On"];
    let Fieldname = ["DeleteIndication", "ItemCode", "ItemDescription", "ItemDetail", "Location", "ContractPeriod", "GSTRate", "ContractStartDate", "ExpiryDate", "CreatedByName", "CreatedOn", "ModifiedByName", "ModifiedOn"];
    exportToExcel(filename, "Data", excelDownoadData, "A1:N1", colName, Fieldname);
    
    // Add log entry for export
    await addLogDetails(
      { Action: "Export", RecordCount: excelData.length },
      "ContractMaster",
      "Export",
      "ExportLog",
      userDetails,
      "Master"
    );
  };

  const handleAdd = () => {
    setAction("Add");
    setShowForm(true);
  };

  return (
    <>
      {loading && <div className="spinner-box"> <Spinner animation="border" className="spinner" /></div>}
      <BreadcrumbComp />
      <Container fluid className="main-container">
        <ActionComp
          label={"Add New Contract"}
          exportEvent={() => handleGlobalExport()}
          handleAdd={handleAdd} />

        <GridMaster
          rowDetails={filteredResult}
          colDetails={columnDefs}
          fieldNames={fieldName}
          headerNames={headerName}
          getDataEvent={(getFilteredData) => setFilteredResulttemp(getFilteredData)}
          getFilterColLength={(getLength) => setFilterColLength(getLength)}
          setExcelData={setExcelData}
          handleEdit={handleEdit}
          resetind={resetFilter}
        />
        {showForm && (
          <AddEditContractMaster
            show={showForm}
            action={action}
            rowdata={rowData}
            allContracts={filteredResult}
            onHide={() => setShowForm(false)}
            setreload={setReload}
            reload={reload}
          />
        )}
      </Container>
    </>
  );
};

export default ContractMaster;